import classNames from 'classnames';
import {observer} from 'mobx-react';
import React, {useMemo} from 'react';

import {useI18n} from '@udemy/i18n';
import {ShoppingClient, ShoppingListName} from '@udemy/shopping';

import {getConfig} from 'src/features/cart/config';

import {ActionSupportedLayoutsProps} from '../shopping-item/types';
import {
    ShoppingList,
    ShoppingListTrackActionsProps,
} from '../shopping-list/shopping-list.react-component';
import styles from './styles.module.less';

interface ShoppingListWrapperProps {
    listName: ShoppingListName;
    sublistType?: string;
    showSaveForLaterAction: boolean;
    trackAction?: ({action, item, destListName}: ShoppingListTrackActionsProps) => void;
    actionsLayout: ActionSupportedLayoutsProps;
    showCodingExercisesBadge: boolean;
}

export const ShoppingListWrapper: React.FC<ShoppingListWrapperProps> = observer(
    ({listName, sublistType, trackAction, ...props}) => {
        const {gettext, interpolate, ngettext} = useI18n();
        const config = getConfig(gettext, interpolate, ngettext);

        const allItems = ShoppingClient.lists[listName as ShoppingListName].items;

        // Memoize filtered items to avoid redundant filtering on every render
        const items = useMemo(
            () =>
                sublistType
                    ? allItems.filter((item) => item.buyable.buyable_object_type === sublistType)
                    : allItems,
            [allItems, sublistType],
        );

        const getTitle = () => {
            if (sublistType) {
                const sublistTitleFn =
                    config.shoppingList.sublistStrings.title[
                        sublistType as keyof typeof config.shoppingList.sublistStrings.title
                    ];
                return sublistTitleFn(items);
            }
            return config.shoppingList.listStrings.title[
                listName as keyof typeof config.shoppingList.listStrings.title
            ](allItems);
        };

        return (
            <div className={styles['shopping-list']}>
                <h3 className={classNames('ud-heading-md', styles['shopping-list-title'])}>
                    {getTitle()}
                </h3>
                <ShoppingList
                    listName={listName}
                    filterByBuyableType={sublistType}
                    performanceMarkName={listName === 'cart' ? config.performanceMarkName : ''}
                    trackingContext={listName === 'cart' ? '' : 'cart-page'}
                    trackAction={trackAction}
                    {...props}
                />
            </div>
        );
    },
);
